import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function ShiftReader() {
  const nav = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(-1); // -1 = intro
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get("/assessment/questions")
      .then((r) => setQuestions(r.data.questions))
      .catch((err) => {
        console.error("Failed to load questions:", err);
        toast.error("Couldn't load the reading. Please refresh.");
      });
  }, []);

  const total = questions.length;
  const q = step >= 0 && step < total ? questions[step] : null;
  const progress = total ? Math.max(0, step + 1) / total : 0;

  const select = (option) => {
    setAnswers((a) => ({ ...a, [q.id]: option }));
    setTimeout(() => {
      if (step < total - 1) setStep(step + 1);
      else submit({ ...answers, [q.id]: option });
    }, 240);
  };

  const submit = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const ordered = questions.map((qq) => finalAnswers[qq.id]);
      const r = await api.post("/assessment/submit", { answers: ordered, name: name || null });
      nav(`/shift-reader/result/${r.data.id}`);
    } catch (e) {
      toast.error("Couldn't generate your profile. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-20 sm:pt-24 min-h-screen aurora grain" data-testid="shift-reader-page">
      <div className="mx-auto max-w-3xl px-5 sm:px-6 py-10 sm:py-16">
        {step === -1 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="eyebrow mb-4 text-accent">The Shift Reader™</div>
            <h1 className="font-serif text-[2.5rem] leading-[1.05] sm:text-6xl mb-5 sm:mb-6">
              Seven questions.
              <br />
              <span className="italic">One mirror.</span>
            </h1>
            <p className="text-muted-foreground text-base sm:text-lg mb-8 sm:mb-10 max-w-xl leading-relaxed">
              This isn't a personality test. It's a diagnostic for the version of you
              that's already becoming. Take your time. The answer you almost said
              is usually the truer one.
            </p>
            <div className="mb-8">
              <label className="eyebrow block mb-2">Your name (optional)</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="How the oracle should address you"
                className="max-w-md h-12"
                data-testid="reader-name-input"
              />
            </div>
            <Button
              size="lg"
              className="w-full sm:w-auto btn-pill font-mono tracking-[0.24em] uppercase text-xs h-12 px-8 gap-2"
              onClick={() => setStep(0)}
              disabled={!questions.length}
              data-testid="reader-begin-btn"
            >
              Begin the reading <ArrowRight className="h-4 w-4" />
            </Button>
          </motion.div>
        )}

        {submitting && (
          <div className="min-h-[70vh] flex flex-col items-center justify-center gap-6 px-4" data-testid="reader-loading">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
            >
              <Sparkles className="h-10 w-10 text-accent" />
            </motion.div>
            <p className="font-serif italic text-lg sm:text-xl text-center max-w-md">
              Reading the frequency you're becoming...
            </p>
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}

        {!submitting && q && (
          <div>
            {/* progress */}
            <div className="mb-8 sm:mb-10 flex items-center gap-4">
              <div className="font-mono text-xs tracking-widest text-accent">
                {String(step + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
              </div>
              <div className="flex-1 h-px bg-border relative overflow-hidden">
                <motion.div
                  className="absolute left-0 top-0 h-full bg-accent"
                  animate={{ width: `${progress * 100}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={q.id}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.4 }}
              >
                <h2 className="font-serif text-2xl sm:text-4xl leading-snug sm:leading-tight mb-8 sm:mb-10" data-testid="reader-question">
                  {q.prompt}
                </h2>
                <div className="grid grid-cols-1 gap-3">
                  {q.options.map((opt) => {
                    const selected = answers[q.id] === opt;
                    return (
                      <button
                        key={opt}
                        onClick={() => select(opt)}
                        className={`text-left p-4 sm:p-5 min-h-[56px] border transition-all group ${
                          selected
                            ? "border-accent bg-accent/10"
                            : "border-border hover:border-accent/40 active:border-accent/60 hover:bg-secondary/30"
                        }`}
                        data-testid={`reader-option-${q.id}-${opt.replace(/\s+/g, "-").toLowerCase().slice(0, 30)}`}
                      >
                        <span className="font-serif text-base sm:text-xl leading-snug">{opt}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-8 sm:mt-10 flex items-center justify-between">
                  <button
                    onClick={() => setStep(Math.max(-1, step - 1))}
                    className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground active:text-foreground flex items-center gap-2 py-3 px-2 -mx-2 min-h-[44px]"
                    data-testid="reader-back-btn"
                  >
                    <ArrowLeft className="h-3 w-3" /> Back
                  </button>
                  <span className="font-mono text-[10px] sm:text-xs uppercase tracking-widest text-muted-foreground italic">
                    Tap to continue
                  </span>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
